/**
 * Recortes de folha, criados uma vez só.
 *
 * Um `Texture` do Pixi que aponta para um pedaço de uma folha se inscreve nela
 * (`source.on('resize')`) e só sai de lá quando alguém o destrói. O tabuleiro
 * criava um recorte novo a cada quadro — a água a cada 400ms, as tochas junto,
 * o passo de cada unidade a cada 260ms — e ninguém os destruía: a folha, que
 * vive no cache do `Assets` a sessão inteira, segurava todos para sempre. Num
 * mapa pequeno eram umas 600 texturas presas por segundo; num de 100×100 com
 * lago, dezenas de milhares. Depois de muitos turnos, o heap não cabia mais.
 *
 * Aqui cada retângulo de cada folha vira textura uma única vez e é
 * compartilhado por quem precisar dele. O total fica limitado pelo tamanho das
 * folhas, e não pela duração da partida.
 *
 * Quem recebe um destes NÃO pode destruí-lo: outros sprites usam o mesmo.
 */

import { Rectangle, Texture, type TextureSource } from 'pixi.js';

const cache = new WeakMap<TextureSource, Map<string, Texture>>();

export function frameTexture(
  source: TextureSource,
  x: number,
  y: number,
  width: number,
  height: number,
): Texture {
  let frames = cache.get(source);
  if (!frames) {
    frames = new Map();
    cache.set(source, frames);
    // Folha descarregada leva os recortes junto: servir um recorte de uma
    // folha destruída desenharia vazio sem aviso.
    source.once('destroy', () => cache.delete(source));
  }

  const key = `${x},${y},${width},${height}`;
  let texture = frames.get(key);
  if (!texture) {
    texture = new Texture({ source, frame: new Rectangle(x, y, width, height) });
    frames.set(key, texture);
  }
  return texture;
}
